import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts'
import {
  Wallet, Loader2, Euro, TrendingUp, Receipt, Percent,
  AlertTriangle, ArrowUp, ArrowDown, Minus,
} from 'lucide-react'

// ==================== Types ====================

interface NetMonth {
  ym: string
  revenue: number
  cost: number
  gross: number
  expenses: number
  net: number
  net_margin: number
  has_expenses: boolean
}

interface NetYear {
  year: string
  revenue: number
  gross: number
  expenses: number
  net: number
  net_margin: number
  months_sales: number
  months_expenses: number
  complete: boolean
}

interface NetSummary {
  months: NetMonth[]
  years: NetYear[]
}

// ==================== Helpers ====================

const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

function fmtCur(v: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
}

function fmtCurFull(v: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v)
}

function monthLabel(ym: string): string {
  return MONTH_NAMES[parseInt(ym.slice(5)) - 1]
}

// ==================== Small components ====================

function Kpi({ label, value, sub, color, icon }: {
  label: string; value: string; sub?: React.ReactNode; color: string; icon: React.ReactNode
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
        <span className={color}>{icon}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{sub}</div>}
    </div>
  )
}

function YoY({ curr, prev }: { curr: number; prev: number | null }) {
  if (prev === null || prev === 0) return null
  const pct = ((curr - prev) / Math.abs(prev)) * 100
  const Icon = pct > 1 ? ArrowUp : pct < -1 ? ArrowDown : Minus
  const cls = pct > 1 ? 'text-emerald-600' : pct < -1 ? 'text-red-500' : 'text-slate-400'
  return (
    <span className={`inline-flex items-center gap-0.5 ${cls}`}>
      <Icon className="w-3 h-3" />{Math.abs(pct).toFixed(1)}% vs año anterior ({fmtCur(prev)})
    </span>
  )
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-slate-900 dark:text-white mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} className="flex justify-between gap-4">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="font-medium text-slate-700 dark:text-slate-200">{fmtCurFull(p.value)}</span>
        </p>
      ))}
    </div>
  )
}

// ==================== Page ====================

export default function NetProfitPage() {
  const [selectedYear, setSelectedYear] = useState<string>('all')

  const query = useQuery({
    queryKey: ['net-profit-summary'],
    queryFn: async () => (await axios.get<NetSummary>('/api/profit-report/net-summary')).data,
    staleTime: 5 * 60 * 1000,
  })

  const data = query.data
  const years = data?.years || []

  const yearData = useMemo(() => {
    if (!data || selectedYear === 'all') return null
    return data.years.find(y => y.year === selectedYear) || null
  }, [data, selectedYear])

  const prevYearData = useMemo(() => {
    if (!data || selectedYear === 'all') return null
    const prev = (parseInt(selectedYear) - 1).toString()
    return data.years.find(y => y.year === prev) || null
  }, [data, selectedYear])

  const monthRows = useMemo(() => {
    if (!data || selectedYear === 'all') return []
    return data.months.filter(m => m.ym.startsWith(selectedYear))
  }, [data, selectedYear])

  // Chart data
  const annualChart = years.map(y => ({
    name: y.year + (y.complete ? '' : ' *'),
    'Beneficio Bruto': y.gross,
    'Gastos Operativos': y.expenses,
    'Beneficio Neto': y.net,
  }))

  const monthlyChart = monthRows.map(m => ({
    name: monthLabel(m.ym) + (m.has_expenses ? '' : ' *'),
    'Beneficio Bruto': m.gross,
    'Gastos Operativos': m.expenses,
    'Beneficio Neto': m.net,
  }))

  // Aggregate for "all years" KPIs (only complete years for the average)
  const totals = useMemo(() => {
    if (selectedYear !== 'all') return yearData
    if (!years.length) return null
    const t = years.reduce((a, y) => ({
      revenue: a.revenue + y.revenue, gross: a.gross + y.gross,
      expenses: a.expenses + y.expenses, net: a.net + y.net,
    }), { revenue: 0, gross: 0, expenses: 0, net: 0 })
    return { ...t, net_margin: t.revenue ? (t.net / t.revenue) * 100 : 0 } as any
  }, [selectedYear, yearData, years])

  const incompleteYears = years.filter(y => !y.complete)
  const missingMonths = monthRows.filter(m => !m.has_expenses)

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Wallet className="w-7 h-7 text-primary-600" />
          Beneficio Neto
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Beneficio bruto por producto (Factusol) menos gastos operativos registrados. Sin transporte ni mercancía: el
          transporte tiene su propia microcontabilidad en Transportistas.
        </p>
      </div>

      {/* Year selector */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setSelectedYear('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedYear === 'all' ? 'bg-primary-600 text-white'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
            }`}>
            Todos los años
          </button>
          {years.map(y => (
            <button key={y.year} onClick={() => setSelectedYear(y.year)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedYear === y.year ? 'bg-primary-600 text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
              }`}>
              {y.year}{!y.complete && ' *'}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          <span className="ml-3 text-slate-500">Cargando datos...</span>
        </div>
      )}
      {query.isError && <div className="card p-6 text-center text-danger-600">Error al cargar los datos.</div>}

      {totals && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Kpi label="Ingresos" value={fmtCur(totals.revenue)} color="text-blue-600 dark:text-blue-400"
              icon={<Euro className="w-4 h-4" />}
              sub={selectedYear !== 'all' && <YoY curr={totals.revenue} prev={prevYearData?.revenue ?? null} />} />
            <Kpi label="Beneficio Bruto" value={fmtCur(totals.gross)} color="text-emerald-600 dark:text-emerald-400"
              icon={<TrendingUp className="w-4 h-4" />}
              sub={selectedYear !== 'all' && <YoY curr={totals.gross} prev={prevYearData?.gross ?? null} />} />
            <Kpi label="Gastos Operativos" value={`-${fmtCur(totals.expenses)}`} color="text-red-500 dark:text-red-400"
              icon={<Receipt className="w-4 h-4" />}
              sub={selectedYear !== 'all' && <YoY curr={totals.expenses} prev={prevYearData?.expenses ?? null} />} />
            <Kpi label="Beneficio Neto" value={fmtCur(totals.net)}
              color={totals.net >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}
              icon={<Percent className="w-4 h-4" />}
              sub={<span>Margen neto: <b>{totals.net_margin.toFixed(1)}%</b>{' '}
                {selectedYear !== 'all' && prevYearData && <YoY curr={totals.net} prev={prevYearData.net} />}</span>} />
          </div>

          {/* Data quality warnings */}
          {selectedYear === 'all' && incompleteYears.length > 0 && (
            <div className="card p-3 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Años con gastos incompletos (*): {incompleteYears.map(y =>
                  `${y.year} (${y.months_expenses}/${y.months_sales} meses)`).join(' · ')}.
                En esos años el beneficio neto real es menor que el mostrado.
              </span>
            </div>
          )}
          {selectedYear !== 'all' && missingMonths.length > 0 && (
            <div className="card p-3 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Meses sin gastos registrados (*): {missingMonths.map(m => monthLabel(m.ym)).join(', ')} —
                su neto mostrado es solo el bruto.
              </span>
            </div>
          )}

          {/* Chart */}
          <div className="card p-5">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-4">
              {selectedYear === 'all' ? 'Evolución anual' : `Evolución mensual ${selectedYear}`}
            </h3>
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={selectedYear === 'all' ? annualChart : monthlyChart} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v: number) => `${Math.round(v / 1000)}k`} tick={{ fontSize: 12 }} width={44} />
                <RechartsTooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                <ReferenceLine y={0} stroke="#94a3b8" />
                <Bar dataKey="Beneficio Bruto" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={46} />
                <Bar dataKey="Gastos Operativos" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={46} />
                <Line type="monotone" dataKey="Beneficio Neto" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="px-4 py-3 text-left">{selectedYear === 'all' ? 'Año' : 'Mes'}</th>
                    <th className="px-4 py-3 text-right">Ingresos</th>
                    <th className="px-4 py-3 text-right">Beneficio Bruto</th>
                    <th className="px-4 py-3 text-right">Gastos</th>
                    <th className="px-4 py-3 text-right">Beneficio Neto</th>
                    <th className="px-4 py-3 text-right">Margen Neto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                  {(selectedYear === 'all'
                    ? years.map(y => ({
                        key: y.year, label: y.year + (y.complete ? '' : ' *'),
                        revenue: y.revenue, gross: y.gross, expenses: y.expenses,
                        net: y.net, margin: y.net_margin,
                      }))
                    : monthRows.map(m => ({
                        key: m.ym, label: `${monthLabel(m.ym)} ${selectedYear}` + (m.has_expenses ? '' : ' *'),
                        revenue: m.revenue, gross: m.gross, expenses: m.expenses,
                        net: m.net, margin: m.net_margin,
                      }))
                  ).map(r => (
                    <tr key={r.key} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-4 py-2.5 font-medium text-slate-900 dark:text-white">{r.label}</td>
                      <td className="px-4 py-2.5 text-right text-slate-600 dark:text-slate-300">{fmtCur(r.revenue)}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-600 dark:text-emerald-400">{fmtCur(r.gross)}</td>
                      <td className="px-4 py-2.5 text-right text-red-500">{r.expenses ? `-${fmtCur(r.expenses)}` : '—'}</td>
                      <td className={`px-4 py-2.5 text-right font-semibold ${r.net >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        {fmtCur(r.net)}
                      </td>
                      <td className={`px-4 py-2.5 text-right ${r.net >= 0 ? 'text-slate-600 dark:text-slate-300' : 'text-red-500'}`}>
                        {r.margin.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-slate-400 dark:text-slate-500">
            Fuentes: beneficio bruto de las ventas por producto de Factusol (solo productos con coste; no incluye portes
            ni servicios sin ficha) y gastos operativos de la sección Gastos (histórico 2021–2026 cargado desde los
            extractos bancarios; sin transporte, mercancía, impuestos ni retiradas del titular).
          </p>
        </>
      )}
    </div>
  )
}
